import { useState } from "react";
import SlideFrame from "./_SlideFrame";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { ArrowRight } from "lucide-react";

const steps = [
  { n: "01", t: "Finalize pricing", d: "Review the commercial model; adjust for your portfolio and volume." },
  { n: "02", t: "Launch a pilot", d: "Board a subset of loans and validate the full servicing workflow end-to-end." },
  { n: "03", t: "Expand", d: "Scale across your portfolio with servicing + optional compliance bundle." },
];

const sellingPoints = [
  { n: "01", t: "Transparent modeling", d: "Flat, per-account pricing — no UPB tax, no surprise line items. Model it before you sign it." },
  { n: "02", t: "Responsible AI + human experts", d: "AI handles the repeatable 80%; operators with consumer-lending scars handle the 20% that matters." },
  { n: "03", t: "Informed innovation", d: "Your servicing data flows back into underwriting, collections strategy, and portfolio decisions — not into a black box." },
  { n: "04", t: "Compliance as an add-on", d: "Instant storytelling for your board, partners, and regulators — replacing weeks of manual tracking with a live posture." },
];

export default function SlideNextSteps({ session }) {
  const [form, setForm] = useState({ company: "", name: "", email: "", role: "", phone: "", message: "" });
  const [sent, setSent] = useState(false);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (!form.company || !form.name || !form.email) {
      toast.error("Company, name, and email required");
      return;
    }
    setLoading(true);
    try {
      await api.post("/leads", { ...form, session_id: session?.session_id });
      setSent(true);
      toast.success("Request sent — we'll be in touch shortly.");
    } catch (err) {
      toast.error("Could not submit. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <SlideFrame tag="10 · Let's Talk">
      <div className="grid lg:grid-cols-12 gap-12 items-start">
        <div className="lg:col-span-6">
          <p className="overline mb-6">What happens next</p>
          <h2 className="font-display text-5xl md:text-7xl tracking-tighter leading-[0.9] font-medium">
            You shouldn't have to choose between <em className="italic text-primary">great tech</em> and a <em className="italic">trusted team</em>.
          </h2>
          <p className="mt-8 text-lg text-foreground/75 leading-relaxed max-w-xl">
            We deliver both. Let&rsquo;s talk about a pilot.
          </p>

          <div className="mt-10 border-t border-foreground/20">
            {steps.map((s) => (
              <div key={s.n} className="grid grid-cols-12 gap-4 py-5 border-b border-foreground/20 items-baseline">
                <span className="col-span-1 font-mono-data text-xs text-muted-foreground">{s.n}</span>
                <span className="col-span-11 md:col-span-4 font-display text-2xl">{s.t}</span>
                <span className="col-span-12 md:col-span-7 text-sm text-muted-foreground">{s.d}</span>
              </div>
            ))}
          </div>

          <div className="mt-10">
            <p className="overline mb-5">Why partners pick IBA</p>
            <div className="grid sm:grid-cols-2 gap-px bg-foreground/20 border border-foreground/20">
              {sellingPoints.map((p) => (
                <div key={p.n} className="bg-background p-5 md:p-6" data-testid={`selling-point-${p.n}`}>
                  <div className="flex items-baseline gap-3 mb-2">
                    <span className="font-mono-data text-[11px] text-primary">{p.n}</span>
                    <h4 className="font-display text-xl leading-tight">{p.t}</h4>
                  </div>
                  <p className="text-sm text-muted-foreground leading-relaxed">{p.d}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 bg-white border border-foreground p-8 md:p-10">
          {sent ? (
            <div className="min-h-[380px] flex flex-col justify-center" data-testid="lead-success">
              <p className="overline mb-4">Received</p>
              <h3 className="font-display text-4xl md:text-5xl leading-tight">
                Thanks, {form.name.split(" ")[0] || "partner"}. <em className="italic text-primary">We&rsquo;ll reach out shortly.</em>
              </h3>
              <p className="mt-6 text-muted-foreground">
                A member of our team will respond within one business day.
              </p>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-6" data-testid="lead-form">
              <p className="overline">Request a pilot</p>
              <h3 className="font-display text-4xl tracking-tight leading-tight">Let&rsquo;s run the numbers on your portfolio.</h3>

              <div className="grid md:grid-cols-2 gap-6 pt-4">
                <Field id="lead-company" label="Company *" value={form.company} onChange={(v) => setForm({ ...form, company: v })} />
                <Field id="lead-name" label="Your name *" value={form.name} onChange={(v) => setForm({ ...form, name: v })} />
                <Field id="lead-email" label="Email *" type="email" value={form.email} onChange={(v) => setForm({ ...form, email: v })} />
                <Field id="lead-role" label="Role" value={form.role} onChange={(v) => setForm({ ...form, role: v })} />
                <Field id="lead-phone" label="Phone" value={form.phone} onChange={(v) => setForm({ ...form, phone: v })} wide />
              </div>

              <div>
                <label htmlFor="lead-message" className="overline block mb-2">Tell us about your portfolio</label>
                <textarea
                  id="lead-message"
                  data-testid="input-lead-message"
                  value={form.message}
                  onChange={(e) => setForm({ ...form, message: e.target.value })}
                  rows={3}
                  className="w-full bg-transparent border border-foreground/30 p-3 text-sm font-mono-data focus:outline-none focus:border-primary"
                />
              </div>

              <button
                type="submit"
                data-testid="submit-lead"
                disabled={loading}
                className="btn-primary w-full md:w-auto disabled:opacity-50"
              >
                {loading ? "Sending…" : "Request a pilot"} <ArrowRight className="w-4 h-4" />
              </button>
              <p className="text-[11px] uppercase tracking-brand text-muted-foreground">
                Confidential · not shared outside IBA Servicing
              </p>
            </form>
          )}
        </div>
      </div>
    </SlideFrame>
  );
}

function Field({ id, label, value, onChange, type = "text", wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <label htmlFor={id} className="overline block mb-2">{label}</label>
      <input
        id={id}
        data-testid={`input-${id}`}
        type={type}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="input-sharp"
      />
    </div>
  );
}
